/**
 * StaffProfileSection (BON-398) — the Profile card of the staff detail page,
 * in both read and edit modes.
 *
 * Split out of StaffEditPage, which had drifted past the lint size and
 * complexity caps; this was the largest single block. The crew-qualifications
 * control lands here rather than anywhere else because "what is this person
 * cleared to work" belongs beside their regions, not on a separate screen.
 *
 * Qualifications are seeded by `valueOf('qualifiedPositions')`, which reads the
 * member field of the same name. Before BON-419 the write field was called
 * `positions` and resolved by name to `member.positions` — the Phase 1.5 rate
 * stub, always empty — which showed every admin an empty control and, on save,
 * revoked everyone's eligibility.
 */
import type { RegionNode } from '../../api/regions';
import type { StaffMemberDetail, StaffPatch } from '../../api/staff';
import type { CrewPosition } from '../../api/workshops';
import { CREW_POSITION_LABELS } from '../../lib/crewPositions';
import { topLevelRegionOptions } from '../../shared/regionAccessOptions';
import RegionAccessPicker from '../../shared/RegionAccessPicker';

import { formatHireDate } from './staffEditModel';

/** The crew positions an admin may grant. Typed so backend-enum drift breaks the build. */
const GRANTABLE_POSITIONS: ReadonlyArray<{ value: CrewPosition; label: string; hint: string }> = [
  { value: 'teacher', label: CREW_POSITION_LABELS.teacher, hint: 'Leads a workshop' },
  { value: 'assistant', label: CREW_POSITION_LABELS.assistant, hint: 'Supports the teacher' },
  {
    value: 'teacher_in_training',
    label: CREW_POSITION_LABELS.teacher_in_training,
    hint: 'Learning to lead',
  },
];

const POSITION_LABELS: Record<string, string> = Object.fromEntries(
  GRANTABLE_POSITIONS.map((p) => [p.value, p.label]),
);

/** Input kinds the shared EditableField renders. */
type FieldType = 'text' | 'email' | 'tel' | 'date' | 'textarea';

export interface StaffProfileSectionProps {
  member: StaffMemberDetail;
  isEditing: boolean;
  regions: RegionNode[];
  fieldErrors: Record<string, string>;
  valueOf: <K extends keyof StaffPatch>(key: K) => StaffPatch[K] | undefined;
  setField: <K extends keyof StaffPatch>(key: K, value: StaffPatch[K]) => void;
  EditableField: React.ComponentType<{
    label: string;
    value: string | null | undefined;
    onChange: (v: string) => void;
    type?: FieldType;
    placeholder?: string;
    error?: string;
  }>;
  DisplayField: React.ComponentType<{ label: string; value: string | null | undefined }>;
}

function QualificationPicker({
  selected,
  onChange,
  error,
}: {
  selected: CrewPosition[];
  onChange: (next: CrewPosition[]) => void;
  error?: string;
}) {
  const toggle = (value: CrewPosition) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <div>
      <p className="block text-xs text-stone-500 dark:text-stone-400 mb-1">Crew qualifications</p>
      <div className="flex flex-col gap-1.5">
        {GRANTABLE_POSITIONS.map((p) => (
          <label key={p.value} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              aria-label={p.label}
              checked={selected.includes(p.value)}
              onChange={() => toggle(p.value)}
              className="rounded border-stone-300 dark:border-stone-600"
            />
            <span className="text-bark-900 dark:text-stone-100">{p.label}</span>
            <span className="text-xs text-stone-400">{p.hint}</span>
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-stone-400">
        Controls which open shifts they can pick up, and which rate their shifts pay at.
      </p>
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

function QualificationDisplay({ positions }: { positions: string[] }) {
  return (
    <div>
      <p className="block text-xs text-stone-500 dark:text-stone-400 mb-1">Crew qualifications</p>
      {positions.length === 0 ? (
        // Not cosmetic: with none granted they see no open shifts at all.
        <p className="text-sm text-stone-400">— none granted, so they can’t pick up shifts</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {positions.map((p) => (
            <span
              key={p}
              className="text-xs px-2 py-0.5 rounded-full bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400"
            >
              {POSITION_LABELS[p] ?? p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The scalar text fields, in render order. Table-driven rather than eighty
 * lines of near-identical JSX — the two bridge controls below are the only
 * parts of this form that differ from one another.
 */
type TextFieldSpec = {
  key: keyof StaffPatch;
  label: string;
  type?: FieldType;
  placeholder?: string;
};

const NAME_FIELDS: TextFieldSpec[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
];

const CONTACT_FIELDS: TextFieldSpec[] = [
  { key: 'displayName', label: 'Display name', placeholder: 'How they appear in workshops' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'fareharborUsername', label: 'FareHarbor username' },
  { key: 'phone', label: 'Phone', type: 'tel' },
];

const TRAILING_FIELDS: TextFieldSpec[] = [
  { key: 'hireDate', label: 'Hire date', type: 'date' },
  { key: 'bio', label: 'Bio', type: 'textarea' },
  {
    key: 'notes',
    label: 'Notes',
    type: 'textarea',
    placeholder: 'Internal notes (not shown to staff)',
  },
];

function ProfileEditFields(props: StaffProfileSectionProps) {
  const { member, regions, fieldErrors, valueOf, setField, EditableField } = props;

  const render = (spec: TextFieldSpec) => (
    <EditableField
      key={spec.key as string}
      label={spec.label}
      type={spec.type}
      placeholder={spec.placeholder}
      value={valueOf(spec.key) as string | null | undefined}
      onChange={(v) => setField(spec.key, v as StaffPatch[typeof spec.key])}
      error={fieldErrors[spec.key as string]}
    />
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">{NAME_FIELDS.map(render)}</div>
      {CONTACT_FIELDS.map(render)}
      <div>
        <p className="block text-xs text-stone-500 dark:text-stone-400 mb-1">Regions</p>
        <RegionAccessPicker
          presentation="popover"
          options={topLevelRegionOptions(regions)}
          selectedIds={
            (valueOf('regionIds') as string[] | undefined) ??
            (member.regions ?? []).map((r) => r.id)
          }
          onChange={(ids) => setField('regionIds', ids)}
          placeholder="— Unassigned —"
        />
        {fieldErrors.regionIds && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.regionIds}</p>
        )}
      </div>
      <QualificationPicker
        selected={valueOf('qualifiedPositions') ?? []}
        onChange={(next) => setField('qualifiedPositions', next)}
        error={fieldErrors.qualifiedPositions}
      />
      {TRAILING_FIELDS.map(render)}
    </div>
  );
}

function ProfileReadFields({
  member,
  DisplayField,
}: Pick<StaffProfileSectionProps, 'member' | 'DisplayField'>) {
  const currentQualifications = member.qualifiedPositions ?? [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <DisplayField label="First name" value={member.firstName} />
        <DisplayField label="Last name" value={member.lastName} />
      </div>
      <DisplayField label="Display name" value={member.displayName} />
      <DisplayField label="Email" value={member.email} />
      <DisplayField label="FareHarbor username" value={member.fareharborUsername} />
      <DisplayField label="Phone" value={member.phone} />
      <div>
        <p className="block text-xs text-stone-500 dark:text-stone-400 mb-1">Regions</p>
        {(member.regions ?? []).length === 0 ? (
          <p className="text-sm text-stone-400">—</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(member.regions ?? []).map((r) => (
              <span
                key={r.id}
                className="text-xs px-2 py-0.5 rounded-full bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400"
              >
                {r.name}
              </span>
            ))}
          </div>
        )}
      </div>
      <QualificationDisplay positions={currentQualifications} />
      <DisplayField label="Hire date" value={formatHireDate(member.hireDate) || null} />
      <DisplayField label="Bio" value={member.bio} />
      <DisplayField label="Notes" value={member.notes} />
    </div>
  );
}

export default function StaffProfileSection(props: StaffProfileSectionProps) {
  return (
    <section className="bg-white dark:bg-[#1e2521] rounded-2xl border border-stone-200 dark:border-stone-700 p-5">
      <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100 mb-4">Profile</h2>
      {props.isEditing ? (
        <ProfileEditFields {...props} />
      ) : (
        <ProfileReadFields member={props.member} DisplayField={props.DisplayField} />
      )}
    </section>
  );
}
